import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Türkçe karakterleri Helvetica-uyumlu ASCII karşılıklarına çevir (PDF için)
const asciiFold = (s) => {
  if (s === null || s === undefined) return "";
  return String(s)
    .replaceAll("İ", "I").replaceAll("ı", "i")
    .replaceAll("Ş", "S").replaceAll("ş", "s")
    .replaceAll("Ç", "C").replaceAll("ç", "c")
    .replaceAll("Ğ", "G").replaceAll("ğ", "g")
    .replaceAll("Ü", "U").replaceAll("ü", "u")
    .replaceAll("Ö", "O").replaceAll("ö", "o")
    .replaceAll("â", "a").replaceAll("Â", "A")
    .replaceAll("₺", "TL");
};

// TL formatı (PDF-safe, ₺ yerine TL suffix)
const formatMoneyPDF = (v) => {
  const num = Number(v) || 0;
  const formatted = new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(num);
  return `${formatted} TL`;
};

// Logo'yu bir kez cache'le
let _logoDataUrlCache = null;
async function getLogoDataUrl() {
  if (_logoDataUrlCache) return _logoDataUrlCache;
  try {
    const res = await fetch("/keskin-logo.png");
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    _logoDataUrlCache = dataUrl;
    return dataUrl;
  } catch {
    return null;
  }
}

/**
 * Export data to Excel (.xlsx)
 */
export function exportToExcel({ sheets = [], filename = "rapor" }) {
  const wb = XLSX.utils.book_new();

  // Metadata sheet (kapak — logo yerine metin)
  const metaData = [
    ["KESKINTUR PORTAL"],
    ["Yönetim Bilgi Sistemi"],
    [""],
    [`Rapor: ${filename}`],
    [`Oluşturulma: ${new Date().toLocaleString("tr-TR")}`],
  ];

  sheets.forEach((s) => {
    const cols = s.columns || [];
    const header = cols.map((c) => c.label);
    const data = s.rows.map((r) => cols.map((c) => {
      const v = typeof c.get === "function" ? c.get(r) : r[c.key];
      if (c.format === "money" || c.format === "TL") return Number(v) || 0;
      if (c.format === "number") return Number(v) || 0;
      return v ?? "";
    }));
    // Başlığa firmayı ekle
    const brand = [
      ["KESKINTUR PORTAL — " + (s.heading || s.name || "Rapor")],
      [`Tarih: ${new Date().toLocaleString("tr-TR")}`],
      [],
    ];
    const ws = XLSX.utils.aoa_to_sheet([...brand, header, ...data]);
    // Auto-width
    ws["!cols"] = cols.map((c) => ({ wch: Math.min(45, Math.max(String(c.label).length + 2, 14)) }));
    // Merge title cell across all cols
    if (cols.length > 1) {
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: cols.length - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: cols.length - 1 } },
      ];
    }
    // Money formatting for money columns
    const range = XLSX.utils.decode_range(ws["!ref"]);
    cols.forEach((c, idx) => {
      if (c.format === "money" || c.format === "TL") {
        for (let R = 4; R <= range.e.r; ++R) {
          const cell = ws[XLSX.utils.encode_cell({ r: R, c: idx })];
          if (cell) cell.z = '#,##0.00" TL"';
        }
      }
    });
    XLSX.utils.book_append_sheet(wb, ws, (s.name || "Sayfa").slice(0, 30));
  });
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

/**
 * Export data to PDF (.pdf) using jsPDF + autoTable
 */
export async function exportToPDF({ title, subtitle, sections = [], filename = "rapor", orientation = "landscape" }) {
  const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Logo (async load, cache)
  let logoWidth = 0;
  try {
    const logo = await getLogoDataUrl();
    if (logo) {
      doc.addImage(logo, "PNG", 40, 25, 40, 40);
      logoWidth = 50; // logo + spacing
    }
  } catch { /* ignore */ }

  // Header text
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(10, 10, 10);
  doc.text("KESKINTUR PORTAL", 40 + logoWidth, 42);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  doc.text(asciiFold(title || "Rapor"), 40 + logoWidth, 58);
  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(asciiFold(subtitle), 40 + logoWidth, 72);
    doc.setTextColor(0);
  }
  const printedAt = new Date().toLocaleString("tr-TR");
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(printedAt, pageWidth - 40, 42, { align: "right" });
  doc.setTextColor(0);

  // Horizontal divider under header
  doc.setDrawColor(200);
  doc.setLineWidth(0.5);
  doc.line(40, 82, pageWidth - 40, 82);

  let cursorY = 100;
  sections.forEach((sec) => {
    if (sec.heading) {
      if (cursorY > doc.internal.pageSize.getHeight() - 80) {
        doc.addPage();
        cursorY = 40;
      }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(10, 10, 10);
      doc.text(asciiFold(sec.heading), 40, cursorY);
      cursorY += 10;
    }
    autoTable(doc, {
      startY: cursorY,
      head: [sec.columns.map((c) => asciiFold(c.label))],
      body: sec.rows.map((r) => sec.columns.map((c) => {
        const v = typeof c.get === "function" ? c.get(r) : r[c.key];
        if (c.format === "money" || c.format === "TL") return formatMoneyPDF(v);
        if (c.format === "number") return new Intl.NumberFormat("tr-TR").format(Number(v) || 0);
        return asciiFold(v ?? "-");
      })),
      styles: { fontSize: 8, cellPadding: 4, font: "helvetica" },
      headStyles: { fillColor: [10, 10, 10], textColor: 255, fontSize: 8, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      margin: { left: 40, right: 40 },
    });
    cursorY = doc.lastAutoTable.finalY + 22;
  });

  // Footer on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Sayfa ${i} / ${pageCount}`, pageWidth - 40, doc.internal.pageSize.getHeight() - 20, { align: "right" });
    doc.text("KESKINTUR PORTAL — Yonetim Bilgi Sistemi", 40, doc.internal.pageSize.getHeight() - 20);
  }

  doc.save(`${filename}.pdf`);
}
